import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { AlertTriangle, Gift, X } from 'lucide-react';
import { bodasApi } from './bodasUtils';

const hayDatos = (q) => q.cliente_id || (q.cliente_correo || '').includes('@') || (q.cliente_telefono || '').replace(/\D/g, '').length >= 7;

// Busca si la persona de la cotización es invitado/referido con beneficio pendiente y precarga su descuento
export function useBeneficioReferido({ habilitado, quote, setQuote }) {
  const [beneficio, setBeneficio] = useState(null);
  const [aviso, setAviso] = useState(null);
  const descartado = useRef(null);
  const anunciado = useRef(null);
  const pctAplicado = useRef(0);
  const { cliente_id, cliente_correo, cliente_telefono } = quote;

  useEffect(() => {
    if (!habilitado) return undefined;
    const t = setTimeout(() => {
      if (!hayDatos({ cliente_id, cliente_correo, cliente_telefono })) { setBeneficio(null); setAviso(null); return; }
      bodasApi.beneficioPendiente({ cliente_id: cliente_id || undefined, correo: cliente_correo || undefined, telefono: cliente_telefono || undefined })
        .then(r => { setBeneficio(r.beneficio); setAviso(r.aviso || null); })
        .catch(() => { setBeneficio(null); setAviso(null); });
    }, 600);
    return () => clearTimeout(t);
  }, [habilitado, cliente_id, cliente_correo, cliente_telefono]);

  useEffect(() => {
    if (!habilitado) return;
    if (!beneficio) {
      const pct = pctAplicado.current;
      pctAplicado.current = 0;
      setQuote(prev => (prev.beneficio_referido_id
        ? { ...prev, beneficio_referido_id: null, descuento_porcentaje: pct && prev.descuento_porcentaje === pct ? 0 : prev.descuento_porcentaje }
        : prev));
      return;
    }
    if (descartado.current === beneficio.solicitud_id) return;
    pctAplicado.current = beneficio.porcentaje || 0;
    setQuote(prev => {
      if (prev.beneficio_referido_id === beneficio.solicitud_id) return prev;
      const pct = beneficio.porcentaje || 0;
      return { ...prev, beneficio_referido_id: beneficio.solicitud_id, descuento_porcentaje: Math.max(prev.descuento_porcentaje || 0, pct) };
    });
    if (anunciado.current === beneficio.solicitud_id) return;
    anunciado.current = beneficio.solicitud_id;
    toast.success(beneficio.porcentaje ? `Beneficio de referido: ${beneficio.porcentaje}% de descuento cargado` : 'Esta persona tiene un beneficio de referido');
  }, [habilitado, beneficio, setQuote]);

  const quitar = () => {
    descartado.current = beneficio?.solicitud_id;
    pctAplicado.current = 0;
    setQuote(prev => ({ ...prev, beneficio_referido_id: null, descuento_porcentaje: prev.descuento_porcentaje === beneficio?.porcentaje ? 0 : prev.descuento_porcentaje }));
  };
  const aplicar = () => {
    descartado.current = null;
    pctAplicado.current = beneficio.porcentaje || 0;
    setQuote(prev => ({ ...prev, beneficio_referido_id: beneficio.solicitud_id, descuento_porcentaje: Math.max(prev.descuento_porcentaje || 0, beneficio.porcentaje || 0) }));
  };
  return { beneficio, aviso, aplicado: !!beneficio && quote.beneficio_referido_id === beneficio.solicitud_id, quitar, aplicar };
}

const origenTexto = (b) => b.origen === 'boda' ? `invitado de la boda ${b.boda_pareja || ''}` : `referido por ${b.referente_nombre || 'un cliente'}`;

const AvisoNoAplica = ({ texto }) => (
  <div className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4" data-testid="cotizacion-beneficio-no-aplica" role="status">
    <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-700" />
    <p className="text-sm text-amber-900"><span className="font-medium">El beneficio de referido no aplica.</span> {texto}</p>
  </div>
);

export const BannerBeneficioReferido = ({ beneficio, aplicado, onQuitar, onAplicar, guardado, aviso }) => {
  const b = guardado || beneficio;
  if (!b) return aviso ? <AvisoNoAplica texto={aviso} /> : null;
  return (
    <div className="flex flex-col gap-3 rounded-xl border border-rose-200 bg-rose-50 p-4 sm:flex-row sm:items-center sm:justify-between" data-testid="cotizacion-beneficio-referido">
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-rose-100"><Gift className="h-5 w-5 text-rose-700" /></div>
        <div>
          <p className="text-sm font-medium text-rose-900">{guardado || aplicado ? `Beneficio aplicado: ${b.texto}` : `Disponible: ${b.texto}`}</p>
          <p className="text-xs text-rose-800">{b.invitado_nombre} · {origenTexto(b)}. Solo aplica en su primera cotización; al venderse, quien lo refirió gana 200 Puntos JPM.</p>
        </div>
      </div>
      {!guardado && (aplicado
        ? <button type="button" onClick={onQuitar} className="inline-flex items-center gap-1 self-start text-xs text-rose-800 underline hover:text-rose-950 sm:self-auto" data-testid="cotizacion-beneficio-quitar"><X className="h-3.5 w-3.5" />No aplicar</button>
        : <button type="button" onClick={onAplicar} className="self-start rounded-lg bg-rose-700 px-3 py-1.5 text-xs font-medium text-white hover:bg-rose-800 sm:self-auto" data-testid="cotizacion-beneficio-aplicar">Aplicar beneficio</button>)}
    </div>
  );
};
